import { useCallback, useEffect, useState } from "react";
import { appApi } from "@/api";
import { CUSTOMER_AUTH_CHANGED_EVENT } from "@/lib/app-notify";
import { auth } from "@/lib/auth";

function normalizeMobile(value: unknown) {
  return String(value || "").trim();
}

export function hasBoundMobile() {
  return Boolean(normalizeMobile(auth.getUserInfo()?.mobile));
}

export function useRequireBoundMobile(enabled = true) {
  const [checkingBoundMobile, setCheckingBoundMobile] = useState(false);
  const [bindMobileRequired, setBindMobileRequired] = useState(false);
  const [mobileBound, setMobileBound] = useState(() => !enabled || hasBoundMobile());

  const markMobileBound = useCallback((mobile: string) => {
    auth.setUserInfo({ ...auth.getUserInfo(), mobile });
    setMobileBound(true);
    setBindMobileRequired(false);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(CUSTOMER_AUTH_CHANGED_EVENT));
    }
  }, []);

  const ensureBoundMobile = useCallback(async () => {
    if (!enabled || !auth.getToken()) {
      setMobileBound(!enabled);
      setBindMobileRequired(false);
      return true;
    }

    if (hasBoundMobile()) {
      setMobileBound(true);
      setBindMobileRequired(false);
      return true;
    }

    setCheckingBoundMobile(true);
    try {
      const userInfo = await appApi.getUserInfo();
      const mobile = normalizeMobile(userInfo?.mobile);
      if (mobile) {
        markMobileBound(mobile);
        return true;
      }
      setMobileBound(false);
      setBindMobileRequired(true);
      return false;
    } catch {
      setMobileBound(false);
      setBindMobileRequired(true);
      return false;
    } finally {
      setCheckingBoundMobile(false);
    }
  }, [enabled, markMobileBound]);

  useEffect(() => {
    let active = true;

    const runCheck = () => {
      ensureBoundMobile().then((bound) => {
        if (!active || bound) {
          return;
        }
        setBindMobileRequired(true);
      });
    };

    runCheck();
    window.addEventListener(CUSTOMER_AUTH_CHANGED_EVENT, runCheck);
    return () => {
      active = false;
      window.removeEventListener(CUSTOMER_AUTH_CHANGED_EVENT, runCheck);
    };
  }, [ensureBoundMobile]);

  return {
    checkingBoundMobile,
    bindMobileRequired,
    mobileBound,
    ensureBoundMobile,
    markMobileBound,
  };
}
